import { 
  collection, 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc, 
  deleteDoc,
  increment, 
  arrayUnion, 
  arrayRemove, 
  serverTimestamp,
  query,
  where,
  getDocs
} from 'firebase/firestore';
import { db } from '../lib/firebase';

// Database structure: vindie/{collection}

export interface VideoInteraction {
  videoId: string;
  userId: string;
  liked: boolean;
  disliked: boolean;
  subscribed: boolean;
  watchTime: number;
  lastWatchedAt: any;
}

export interface VideoStats {
  likes: number;
  dislikes: number;
  views: number;
  subscribers: number;
  totalWatchTime: number;
}

export interface Donation {
  id: string;
  fromUserId: string;
  toUserId: string;
  videoId: string;
  amount: number;
  message?: string;
  createdAt: any;
}

export class VideoInteractionService {
  // Get or create user interaction document
  static async getUserInteraction(videoId: string, userId: string): Promise<VideoInteraction | null> {
    try {
      const interactionRef = doc(db, 'videoInteractions', `${videoId}_${userId}`);
      const interactionSnap = await getDoc(interactionRef);
      
      if (interactionSnap.exists()) {
        const data = interactionSnap.data() as VideoInteraction;
        return data;
      }
      
      // Create new interaction document
      const newInteraction: VideoInteraction = {
        videoId,
        userId,
        liked: false,
        disliked: false,
        subscribed: false,
        watchTime: 0,
        lastWatchedAt: serverTimestamp()
      };
      
      await setDoc(interactionRef, newInteraction);
      return newInteraction;
    } catch (error) {
      // Return a default interaction object instead of null
      return {
        videoId,
        userId,
        liked: false,
        disliked: false,
        subscribed: false,
        watchTime: 0,
        lastWatchedAt: new Date()
      };
    }
  }

  // Update like status - YouTube style logic
  static async toggleLike(videoId: string, userId: string, isLiked: boolean): Promise<boolean> {
    try {
      const interactionRef = doc(db, 'videoInteractions', `${videoId}_${userId}`);
      const videoStatsRef = doc(db, 'videoStats', videoId);

      // Get current user interaction state
      const interactionSnap = await getDoc(interactionRef);
      const currentInteraction = interactionSnap.exists() ? interactionSnap.data() : null;
      const wasLiked = currentInteraction?.liked || false;
      const wasDisliked = currentInteraction?.disliked || false;

      // Get current video stats
      const videoStatsSnap = await getDoc(videoStatsRef);
      const currentStats = videoStatsSnap.exists() ? videoStatsSnap.data() : null;
      const currentLikes = currentStats?.likes || 0;
      const currentDislikes = currentStats?.dislikes || 0;

      // Calculate the changes needed
      let likeChange = 0;
      let dislikeChange = 0;

      if (isLiked && !wasLiked) {
        // User is liking (was neutral or disliked)
        likeChange = 1;
        if (wasDisliked) {
          dislikeChange = -1; // Remove the dislike
        }
      } else if (!isLiked && wasLiked) {
        // User is unliking (was liked)
        likeChange = -1;
      }

      // Update user interaction
      if (!interactionSnap.exists()) {
        await setDoc(interactionRef, {
          videoId,
          userId,
          liked: isLiked,
          disliked: false,
          watchTime: 0,
          lastWatchedAt: serverTimestamp()
        });
      } else {
        await updateDoc(interactionRef, {
          liked: isLiked,
          disliked: false, // Always set disliked to false when liking
          lastWatchedAt: serverTimestamp()
        });
      }

      // Update video stats
      if (!videoStatsSnap.exists()) {
        await setDoc(videoStatsRef, {
          videoId,
          likes: Math.max(0, likeChange),
          dislikes: Math.max(0, -dislikeChange),
          views: 0
        });
      } else {
        const updates: any = {};
        if (likeChange !== 0) {
          updates.likes = increment(likeChange);
        }
        if (dislikeChange !== 0) {
          updates.dislikes = increment(dislikeChange);
        }
        
        if (Object.keys(updates).length > 0) {
          await updateDoc(videoStatsRef, updates);
        }
      }

      return true;
    } catch (error) {
      console.error('Error toggling like:', error);
      return false;
    }
  }

  // Update dislike status - YouTube style logic
  static async toggleDislike(videoId: string, userId: string, isDisliked: boolean): Promise<boolean> {
    try {
      const interactionRef = doc(db, 'videoInteractions', `${videoId}_${userId}`);
      const videoStatsRef = doc(db, 'videoStats', videoId);

      // Get current user interaction state
      const interactionSnap = await getDoc(interactionRef);
      const currentInteraction = interactionSnap.exists() ? interactionSnap.data() : null;
      const wasLiked = currentInteraction?.liked || false;
      const wasDisliked = currentInteraction?.disliked || false;

      // Get current video stats
      const videoStatsSnap = await getDoc(videoStatsRef);
      const currentStats = videoStatsSnap.exists() ? videoStatsSnap.data() : null;
      const currentLikes = currentStats?.likes || 0;
      const currentDislikes = currentStats?.dislikes || 0;

      // Calculate the changes needed
      let likeChange = 0;
      let dislikeChange = 0;

      if (isDisliked && !wasDisliked) {
        // User is disliking (was neutral or liked)
        dislikeChange = 1;
        if (wasLiked) {
          likeChange = -1; // Remove the like
        }
      } else if (!isDisliked && wasDisliked) {
        // User is undisliking (was disliked)
        dislikeChange = -1;
      }

      // Update user interaction
      if (!interactionSnap.exists()) {
        await setDoc(interactionRef, {
          videoId,
          userId,
          liked: false,
          disliked: isDisliked,
          watchTime: 0,
          lastWatchedAt: serverTimestamp()
        });
      } else {
        await updateDoc(interactionRef, {
          liked: false, // Always set liked to false when disliking
          disliked: isDisliked,
          lastWatchedAt: serverTimestamp()
        });
      }

      // Update video stats
      if (!videoStatsSnap.exists()) {
        await setDoc(videoStatsRef, {
          videoId,
          likes: Math.max(0, -likeChange),
          dislikes: Math.max(0, dislikeChange),
          views: 0
        });
      } else {
        const updates: any = {};
        if (likeChange !== 0) {
          updates.likes = increment(likeChange);
        }
        if (dislikeChange !== 0) {
          updates.dislikes = increment(dislikeChange);
        }
        
        if (Object.keys(updates).length > 0) {
          await updateDoc(videoStatsRef, updates);
        }
      }

      return true;
    } catch (error) {
      console.error('Error toggling dislike:', error);
      return false;
    }
  }

  // Update subscription status
  static async toggleSubscription(creatorId: string, userId: string, isSubscribed: boolean): Promise<boolean> {
    try {
      const subscriptionRef = doc(db, 'subscriptions', `${userId}_${creatorId}`);
      
      if (isSubscribed) {
        // Subscribe - add subscription document
        await setDoc(subscriptionRef, {
          userId,
          creatorId,
          subscribedAt: serverTimestamp()
        });
      } else {
        // Unsubscribe - delete subscription document
        await deleteDoc(subscriptionRef);
      }

      // Update creator's subscriber count
      const creatorStatsRef = doc(db, 'creatorStats', creatorId);
      
      // Check if the document exists, if not create it
      const statsSnap = await getDoc(creatorStatsRef);
      if (!statsSnap.exists()) {
        await setDoc(creatorStatsRef, {
          subscribers: isSubscribed ? 1 : 0
        });
      } else {
        await updateDoc(creatorStatsRef, {
          subscribers: increment(isSubscribed ? 1 : -1)
        });
      }

      return true;
    } catch (error) {
      console.error('Error toggling subscription:', error);
      return false;
    }
  }

  // Record watch time
  static async recordWatchTime(videoId: string, userId: string, watchTime: number): Promise<boolean> {
    try {
      const interactionRef = doc(db, 'videoInteractions', `${videoId}_${userId}`);
      
      await updateDoc(interactionRef, {
        watchTime: increment(watchTime),
        lastWatchedAt: serverTimestamp()
      });

      // Update video stats
      const videoStatsRef = doc(db, 'videoStats', videoId);
      await updateDoc(videoStatsRef, {
        totalWatchTime: increment(watchTime)
      });

      return true;
    } catch (error) {
      console.error('Error recording watch time:', error);
      return false;
    }
  }

  // Record video view
  static async recordView(videoId: string, userId: string, watchDuration: number = 0): Promise<boolean> {
    try {
      const videoStatsRef = doc(db, 'videoStats', videoId);
      
      // Check if video stats document exists, if not create it
      const videoStatsSnap = await getDoc(videoStatsRef);
      if (!videoStatsSnap.exists()) {
        await setDoc(videoStatsRef, {
          videoId,
          likes: 0,
          dislikes: 0,
          views: 1
        });
      } else {
        await updateDoc(videoStatsRef, {
          views: increment(1)
        });
      }

      // Record unique view (YouTube-style: one view per user per video)
      const viewRef = doc(db, 'videoViews', `${videoId}_${userId}`);
      await setDoc(viewRef, {
        videoId,
        userId,
        viewedAt: serverTimestamp(),
        watchTime: watchDuration
      });

      return true;
    } catch (error) {
      // Silently fail - view recording is non-critical
      return false;
    }
  }

  // Get video stats
  static async getVideoStats(videoId: string): Promise<VideoStats | null> {
    try {
      const videoStatsRef = doc(db, 'videoStats', videoId);
      const videoStatsSnap = await getDoc(videoStatsRef);
      
      if (videoStatsSnap.exists()) {
        const data = videoStatsSnap.data() as VideoStats;
        return data;
      }
      
      // Initialize video stats if they don't exist
      const initialStats: VideoStats = {
        likes: 0,
        dislikes: 0,
        views: 0,
        subscribers: 0,
        totalWatchTime: 0
      };
      
      await setDoc(videoStatsRef, initialStats);
      return initialStats;
    } catch (error) {
      // Return default stats instead of null
      return {
        likes: 0,
        dislikes: 0,
        views: 0,
        subscribers: 0,
        totalWatchTime: 0
      };
    }
  }

  // Get creator stats
  static async getCreatorStats(creatorId: string): Promise<any> {
    try {
      const statsRef = doc(db, 'creatorStats', creatorId);
      const statsSnap = await getDoc(statsRef);
      
      if (statsSnap.exists()) {
        return statsSnap.data();
      }
      
      return null;
    } catch (error) {
      console.error('Error getting creator stats:', error);
      return null;
    }
  }

  // Create donation
  static async createDonation(
    fromUserId: string, 
    toUserId: string, 
    videoId: string, 
    amount: number, 
    message?: string
  ): Promise<boolean> {
    try {
      const donationRef = doc(collection(db, 'donations'));
      
      const donation: Donation = {
        id: donationRef.id,
        fromUserId,
        toUserId,
        videoId,
        amount,
        message,
        createdAt: serverTimestamp()
      };
      
      await setDoc(donationRef, donation);

      // Update creator's earnings
      const creatorStatsRef = doc(db, 'creatorStats', toUserId);
      await updateDoc(creatorStatsRef, {
        totalEarnings: increment(amount),
        donationsReceived: increment(1)
      });

      return true;
    } catch (error) {
      console.error('Error creating donation:', error);
      return false;
    }
  }

  // Get user's subscriptions
  static async getUserSubscriptions(userId: string): Promise<string[]> {
    try {
      const subscriptionsRef = collection(db, 'subscriptions');
      const q = query(subscriptionsRef, where('userId', '==', userId));
      const querySnapshot = await getDocs(q);
      
      const subscriptions: string[] = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        if (data.subscribedAt) {
          subscriptions.push(data.creatorId);
        }
      });
      
      return subscriptions;
    } catch (error) {
      console.error('Error getting user subscriptions:', error);
      return [];
    }
  }

  // Check if user has already viewed a video (for YouTube-style unique view tracking)
  static async hasUserViewedVideo(videoId: string, userId: string): Promise<boolean> {
    try {
      const viewRef = doc(db, 'videoViews', `${videoId}_${userId}`);
      const viewSnap = await getDoc(viewRef);
      return viewSnap.exists();
    } catch (error) {
      console.error('Error checking if user viewed video:', error);
      return false;
    }
  }
}

